/**
 * One time-ordered stream of what the agents have been doing.
 *
 * THE RULE THIS FILE EXISTS TO ENFORCE: nothing enters the feed without a real
 * timestamp from the server. A list sorted by time whose rows carry no time is
 * not a feed — it is the roster in disguise, and it would read as "these agents
 * have been quiet" when the truth is "nobody wrote down when they spoke". So
 * anything derived from `/agent/search` stays out: it returns WHICH sessions
 * matched and never when.
 *
 * What IS here, and where each field comes from:
 *
 *   project  /project/history — ts (epoch SECONDS, `int(time.time())`), the
 *            author in `from_ref` ({agent: hash, label} or {email}), the body.
 *            Full history, so a project genuinely contributes many rows.
 *   bus      /agent/activity — the last 200 agent-to-agent deliveries with their
 *            SENDER. Memory-only on the server by design, so it empties on
 *            restart; that is the same trade `_INBOUND` already makes and the
 *            alternative was writing message content to disk for a feed.
 *   inbox    /agent/idle_status `last_msg_ts` — the newest delivery per agent,
 *            which is the tail the 200-event bus window has dropped.
 *   said     /agent/idle_status `last_said_ts` — what each agent last said in its
 *            own terminal, from the transcript LINE's own timestamp rather than
 *            the file's mtime (mtime moves on any append, so it would date the
 *            agent's words to a moment it did not speak).
 *
 * ALL FOUR ARRIVED BY ASKING. The first version of this file could only draw
 * project posts and one anonymous row per agent, and said so on screen;
 * avengents-server added the sender hash, the bus log and `last_said_ts` in one
 * pass. Nothing here guesses in their absence — a row without a timestamp is
 * dropped — so the page improved when they landed instead of breaking.
 */
import { machineNameOf } from '../agents/machineName'
import { relativeAge } from '../ui/relativeTime'
import { OWNER_SENDER } from '../transport/api'
import type { BusEvent, IdleAgent, ProjectMessage, ProjectRow } from '../transport/api'

/**
 * A message the OWNER sent, named as the owner.
 *
 * `OWNER_SENDER` is a sentinel, not an address, so it resolves against nothing
 * and both lanes below printed its first eight characters — "__owner_" — as the
 * author of the owner's own words. Same fault the owner reported as
 * 「這上面提及的 someone 其實就是我自己」, in a second place and with a second
 * spelling. The agent transcript already draws these as "you".
 *
 * Returns null for every other sender so the callers read as a guard, and the
 * HASH is dropped with it: seeding a generated avatar from a sentinel draws a
 * confident face for a person who has one.
 */
function ownerRow(hash: string | null): { author: string; authorHash: null } | null {
  return hash === OWNER_SENDER ? { author: 'You', authorHash: null } : null
}

export type ActivityKind = 'project' | 'agent'

export type ActivityItem = {
  /** Stable across refetches: a message id, or `agent:host\0session`. */
  key: string
  kind: ActivityKind
  /** Epoch SECONDS — the unit both sources use. Never milliseconds. */
  ts: number
  /**
   * Channel hash of the AUTHOR when an agent wrote it, else null.
   *
   * Null is never padded with a placeholder: this seeds the PixelPet, and a
   * made-up hash draws a confident face for someone who has none — the same
   * rule ProjectRow.last_from_hash is documented under.
   */
  authorHash: string | null
  /** Who the row is about, as a person reads it. */
  author: string
  /** The room or session it happened in, without the leading glyph. */
  where: string
  /** Second line, Slack's "Post in #channel" slot. */
  context: string
  /** What tapping the row opens. */
  open:
    | { kind: 'project'; id: string }
    | { kind: 'dm'; peer: string }
    | { kind: 'agent'; session: string; host: string | null }
  /** The message, already collapsed to one line by the server. */
  text: string
  /** Unread BY THE OWNER — see markUnread for what that can and cannot mean. */
  unread: boolean
}

/** Newest messages to pull per project when the panel opens. */
export const ACTIVITY_PAGE = 30
/** Hard ceiling on rendered rows; the panel says so when it bites. */
export const ACTIVITY_MAX = 200

/**
 * Collapse a body to one line for a feed row.
 *
 * The server previews `last_msg_preview` itself (80 chars, whitespace folded);
 * project bodies arrive whole, so they are folded here. Length is left to CSS —
 * `-webkit-line-clamp` knows the column width and this does not, and cutting at
 * a character count as well would clip mid-word before the clamp ever ran.
 */
/* Internal: five call sites in this file. Was exported for the voice path's
 * speak-this-aloud text, which was deleted with the voice stack on 2026-09-02. */
function oneLine(body: string | null | undefined): string {
  return (body ?? '').replace(/\s+/g, ' ').trim()
}

/**
 * A file card's body is JSON, not prose. The feed shows the caption if there is
 * one, and names the attachment otherwise — rendering raw JSON in a preview is
 * how a row becomes noise that people learn to skip.
 */
function bodyText(body: string | null | undefined): string {
  const raw = oneLine(body)
  if (!raw.startsWith('{') || !raw.includes('"type"')) return raw
  try {
    const card = JSON.parse(raw) as {
      type?: string
      text?: string
      attachments?: { name?: string }[]
    }
    if (card.type !== 'file') return raw
    const caption = oneLine(card.text)
    const names = (card.attachments ?? []).map((a) => a.name).filter(Boolean)
    if (caption) return caption
    return names.length ? `📎 ${names.join(', ')}` : '📎 shared a file'
  } catch {
    return raw
  }
}

/**
 * Project messages as feed rows, newest-unread marked.
 *
 * `unread` on a ProjectRow is a COUNT for the room, not a flag per message, and
 * the server defines it as "authored by someone else and newer than the owner's
 * cursor". Since that set is bounded by a timestamp, the unread messages are
 * exactly the newest N written by someone other than the owner — which is
 * derivable here without a second round trip. Two honest limits:
 *   - if the room has more unread than the page loaded, only what is loaded can
 *     be marked; the count in the sidebar stays authoritative.
 *   - a message whose body could not be decrypted is omitted by the server, so
 *     it can be counted and not shown. It is one row's worth of drift, and the
 *     alternative — inventing a row for a body we cannot read — is worse.
 */
export function projectActivity(
  projects: ProjectRow[],
  byProject: Map<string, ProjectMessage[]>,
  selfEmail: string | null,
): ActivityItem[] {
  const out: ActivityItem[] = []
  for (const p of projects) {
    const msgs = byProject.get(p.project_id)
    if (!msgs?.length) continue
    const room = p.name ?? p.project_id
    const items = msgs.map((m) => {
      const hash = typeof m.from_ref?.agent === 'string' ? m.from_ref.agent : null
      const email = m.from_ref?.email ?? null
      return {
        key: m.message_id,
        kind: 'project' as const,
        ts: m.ts,
        authorHash: hash,
        author: m.from_ref?.label ?? email ?? (hash ? hash.slice(0, 8) : 'unknown'),
        where: room,
        context: `Post in ${room}`,
        // A DM opens by PEER, a project by id — the row carries the difference so
        // the panel does not have to guess it back from a title.
        open: p.dm_peer
          ? ({ kind: 'dm', peer: p.dm_peer } as const)
          : ({ kind: 'project', id: p.project_id } as const),
        text: bodyText(m.body),
        // Filled in below; a message is not unread on its own account.
        unread: false,
        mine: !hash && !!email && email === selfEmail,
      }
    })
    const want = typeof p.unread === 'number' ? p.unread : 0
    if (want > 0) {
      // Newest first among messages someone ELSE wrote — see the doc comment.
      const others = items
        .filter((i) => !i.mine)
        .sort((a, b) => b.ts - a.ts)
        .slice(0, want)
      for (const i of others) i.unread = true
    }
    for (const { mine: _mine, ...item } of items) out.push(item)
  }
  return out
}

/**
 * ONE delivery's identity, shared by the two sources that can describe it.
 *
 * `/agent/activity` and `/agent/idle_status` are not two stores: the deque entry
 * and `_INBOUND[session]` are written in the same statement from the same
 * `time.time()` value, so a delivery seen through either lens has the identical
 * timestamp. Emitting the same key from both — and letting mergeActivity keep the
 * first — is what stops the newest message appearing twice, once with its sender
 * and once without.
 *
 * Rounded to milliseconds so the key survives any float formatting difference
 * between the two responses. They come from one value today; the key should not
 * depend on that staying true to the microsecond.
 */
function busKey(host: string | null, session: string, ts: number): string {
  return `bus:${host ?? ''}\0${session}:${ts.toFixed(3)}`
}

/**
 * The agent bus as a real feed: who messaged whom, when, and what it said.
 *
 * The row is about the SENDER — that is the whole point of `from_hash`, which the
 * server was parsing and discarding until this page needed it. Before it, every bus
 * row could only be about the receiver, i.e. about the one agent that certainly did
 * not write the message.
 *
 * Tapping opens the RECEIVER, because that is where the text landed and where you
 * would go to read the rest of it.
 */
export function busActivity(events: BusEvent[]): ActivityItem[] {
  const out: ActivityItem[] = []
  for (const e of events) {
    if (typeof e.ts !== 'number' || !e.ts) continue
    const host = e.to_host ?? null
    const owner = ownerRow(e.from_hash || null)
    out.push({
      key: busKey(host, e.to_session, e.ts),
      kind: 'agent',
      ts: e.ts,
      // The SENDER's face. Null would be a hash this edge cannot resolve.
      authorHash: owner ? owner.authorHash : e.from_hash || null,
      /*
       * Resolved server-side, and the fallback to the raw hash is deliberate
       * rather than a default: the registry lives on the edge, so an unresolvable
       * hash means "from somewhere this machine does not know", and printing it is
       * the honest render of that. It is also what the readable-sender work
       * removed from the terminal, so it should be rare.
       */
      author: owner?.author ?? (e.from || (e.from_hash ? e.from_hash.slice(0, 8) : 'unknown')),
      where: e.to_session,
      context: `Message to ${e.to_session}${host ? ` @ ${host}` : ''}`,
      open: { kind: 'agent', session: e.to_session, host },
      text: oneLine(e.preview),
      // The bus log carries no read state; unread lives per session on the roster.
      unread: false,
    })
  }
  return out
}

/**
 * The newest delivery per agent, from the roster — the tail the bus log has lost.
 *
 * `_BUS` is capped at 200 events, so an agent whose last message is older than that
 * window falls out of it entirely while `_INBOUND` still remembers it. This keeps
 * that row. Same key as the bus event for the same delivery, so when both describe
 * it, the richer one wins.
 */
export function inboxActivity(agents: IdleAgent[]): ActivityItem[] {
  const byHash = new Map<string, IdleAgent>()
  for (const a of agents) if (a.hash) byHash.set(a.hash, a)

  const out: ActivityItem[] = []
  for (const a of agents) {
    const ts = typeof a.last_msg_ts === 'number' ? a.last_msg_ts : 0
    // No timestamp ⇒ not on a timeline. See the rule at the top of this file.
    if (!ts) continue
    const host = a.host ?? null
    const count = typeof a.msg_count === 'number' ? a.msg_count : 0
    const fromHash = a.last_msg_from ?? null
    // The roster sends the sender's HASH; the readable name is resolved here
    // because the client already holds the roster it would be looked up in.
    const sender = fromHash ? byHash.get(fromHash) : undefined
    /*
     * A PERSON's name, when a person wrote it — owner, 2026-09-21:
     * 「需要把使用者 The identity 身分…都要放進去」.
     *
     * Only a human sender produces this. An agent's message names itself through
     * `last_msg_from`, so the two are never both the subject: the hash wins below
     * because it can also be resolved to a face, and this fills the case that had
     * no name at all.
     */
    const person = (a.last_msg_from_name ?? '').trim()
    const owner = ownerRow(fromHash)
    out.push({
      key: busKey(host, a.session, ts),
      kind: 'agent',
      ts,
      authorHash: owner ? owner.authorHash : fromHash,
      /*
       * THE REAL SENDER, OR NOBODY — owner, 2026-09-21, pointing at a row titled
       * "someone" over his own message: 「這上面提及的 someone 其實就是我自己」, and
       * then: 「需要把使用者 The identity 身分…都要放進去」.
       *
       * Four sources, in the order of how firmly each names a real person:
       *   owner    the `__owner__` sentinel — see ownerRow. First because it is
       *            the only one that is certain, and because resolving it as a
       *            hash is what printed "__owner_" over the owner's own words.
       *   sender   an agent hash resolved against this client's own roster — the
       *            only one that also yields a face.
       *   person   `last_msg_from_name`, the display name the chat service
       *            already knew and the edge had been discarding. This is the
       *            case the owner hit: a human typing in Project Chat or Friend
       *            Chat has no hash, so nothing above could ever name him.
       *   neither  the honest subject is the agent whose inbox it is — which is
       *            also whose face the row draws (authorHash is null, so
       *            ActivityRow seeds the avatar on `where`), and the context
       *            below stops saying "Message to <agent>" because the title now
       *            carries it.
       *
       * The literal "someone" is gone for good: it was a placeholder the edge's
       * chat.feed handler supplies when the cloud sends no name, and printing a
       * placeholder as a person is how the owner's own words came to be filed
       * under a stranger.
       */
      author:
        owner?.author ?? (sender?.session ?? (person || (fromHash ? fromHash.slice(0, 8) : a.session))),
      where: a.session,
      /*
       * `N total` is the running count of agent-bus messages this agent has
       * received, and it belongs on THIS row rather than on a bus row: a bus row
       * is one specific message, where a total reads as a property of that
       * message. Here the row already IS "the newest thing this agent received",
       * so the total is the honest second fact about it.
       *
       * It was rendered when this was one function, and the rewrite into
       * bus/inbox dropped the use while the field survived in the type AND in a
       * test fixture — which is why nothing noticed. Found by scanning for
       * declared fields nothing reads, after avengents-server swept their side
       * for the same shape.
       */
      context:
        (fromHash || sender || person
          ? `Message to ${a.session}${host ? ` @ ${host}` : ''}`
          : `Received a message${host ? ` @ ${host}` : ''}`) +
        (count > 1 ? ` · ${count} total` : ''),
      open: { kind: 'agent', session: a.session, host },
      /*
       * ⛔ NO VAULT EXPLANATION. This said "Hidden while the vault is locked"
       * for ANY empty preview, and the owner met it on his own message
       * (2026-09-21). Measured on the machine that served that row:
       * /security/status answers configured:false, enforcement:false,
       * unlocked:true and the roster's own `last_said_reason` is "ok" — the
       * gate never ran. The preview was simply absent, and the feed invented a
       * reason for it.
       *
       * An empty preview now shows NOTHING. The row still carries who, where
       * and when; a missing sentence is a missing sentence, and naming a cause
       * the client cannot know is how a reader is sent to fix the wrong thing.
       *
       * WHY IT WAS ABSENT, found the same day and fixed on the edge: the
       * recorded preview was trimmed off the inbound row on the way to disk,
       * and that row is rebuilt from disk on every read — so `last_msg_preview`
       * was null for every agent, always. Chat frames were also previewed
       * whole, markup and reply-manual included. Both are fixed upstream; this
       * branch stays because an empty preview must still render as empty.
       */
      text: oneLine(a.last_msg_preview),
      unread: (a.unread ?? 0) > 0,
    })
  }
  return out
}

/**
 * What each agent last SAID in its own terminal.
 *
 * This is the row the owner asked for and the one the feed could not draw at all
 * until avengents-server added `last_said_ts`. One per agent — the roster is a
 * roster, not a log — so an agent that has spoken forty times today contributes its
 * latest line, and the transcript view is where the rest lives.
 *
 * A NON-NULL TIMESTAMP IS THE ATTRIBUTION GUARANTEE, and that is what makes the
 * locked case safe to draw. `last_said` only produces a ts on its `ok` path: every
 * branch that cannot say whose words these are — `transcript_ambiguous` (two
 * transcripts under one cwd, so newest-file resolution is a coin flip),
 * `transcript_stale`, `no_transcript`, `nothing_yet` — returns null for it. The
 * vault gate then overwrites the REASON to `vault_locked` while deliberately
 * letting the timestamp through, so under lock the reason can no longer tell the
 * cases apart — but the timestamp still can. Filtering on `ts` rather than on
 * `reason === 'ok'` is therefore not a shortcut; it is the only test that keeps
 * working once the gate has spoken.
 */
export function saidActivity(agents: IdleAgent[]): ActivityItem[] {
  const out: ActivityItem[] = []
  for (const a of agents) {
    const ts = typeof a.last_said_ts === 'number' ? a.last_said_ts : 0
    if (!ts) continue
    const host = a.host ?? null
      // The ACCOUNT's name for the machine. IdleAgent has carried device_name since
      // the roster fan-out began stamping it; this printed the router's word for a
      // RELATIONSHIP because nothing here had been changed to ask for the name.
      const where = machineNameOf(a)
    out.push({
      key: `said:${host ?? ''}\0${a.session}:${ts.toFixed(3)}`,
      kind: 'agent',
      ts,
      authorHash: a.hash ?? null,
      author: a.session,
      where: where,
      context: `Said in its terminal · ${where}`,
      open: { kind: 'agent', session: a.session, host },
      // Same rule as the inbox row above: an absent line shows nothing rather
      // than a vault that does not exist. `last_said_reason` is what actually
      // says WHY the edge had no text, and it is a different sentence per cause.
      text: oneLine(a.last_said_preview),
      // "Said" is not addressed to anyone, so it is never unread — the roster's
      // unread counts inbound messages, and reusing it here would put the same
      // badge on two different rows for two different reasons.
      unread: false,
    })
  }
  return out
}

/**
 * Everything, newest first.
 *
 * Ties break on `key` rather than being left to sort stability: project ts is
 * whole seconds, so a burst of messages genuinely shares a timestamp, and two
 * renders that disagree about their order make the list twitch under the thumb.
 */
export function mergeActivity(...lists: ActivityItem[][]): ActivityItem[] {
  const seen = new Set<string>()
  const all: ActivityItem[] = []
  for (const list of lists) {
    for (const item of list) {
      if (seen.has(item.key)) continue
      seen.add(item.key)
      all.push(item)
    }
  }
  all.sort((a, b) => b.ts - a.ts || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
  return all
}

/**
 * The number on the Activity tab — how much is waiting for the owner.
 *
 * Summed from the two lists the app ALREADY polls (the project rows and the idle
 * roster) rather than from the feed's own rows, because the tab has to be right
 * while the panel is closed and the feed's queries are not running. It is also the
 * authoritative count: a room can hold more unread than the page this feed loaded,
 * so the rows can only ever mark a subset, and the badge must not shrink to match
 * what happens to be on screen.
 */
export function activityUnread(projects: ProjectRow[], agents: IdleAgent[]): number {
  let n = 0
  for (const p of projects) n += typeof p.unread === 'number' ? p.unread : 0
  for (const a of agents) n += a.unread ?? 0
  return n
}

/**
 * `queue` is not a filter over the same rows — it is a different list entirely.
 *
 * The other four narrow the activity feed: things that HAVE happened. Queue shows
 * messages that have NOT happened yet and are waiting for a recipient to stop being
 * busy. It lives here because Activity is already the panel for "what needs my
 * attention", and because only a person can clear a queue — so it has to be reachable
 * from wherever they are, not from inside the view they must leave to end the block.
 */
export type ActivityTab = 'all' | 'agents' | 'projects' | 'unread' | 'queue'

export const ACTIVITY_TABS: { id: ActivityTab; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'agents', label: 'Agents' },
  /*
   * "Chat", not "Projects" — owner, 2026-08-22: Project 那個欄位應該可以改名為 Chat
   * 這樣也包含 friend 1-1 chat. The filter covers every CONVERSATION now, project
   * rooms and friend DMs alike, and "Projects" would have been a label that excluded
   * half of what the list under it holds. The tab's ID stays `projects`: that is the
   * wiring, not a word anybody reads.
   */
  { id: 'projects', label: 'Chat' },
  { id: 'unread', label: 'Unread' },
  { id: 'queue', label: 'Queue' },
]

export function filterActivity(items: ActivityItem[], tab: ActivityTab): ActivityItem[] {
  switch (tab) {
    case 'agents':
      return items.filter((i) => i.kind === 'agent' || i.authorHash !== null)
    case 'projects':
      return items.filter((i) => i.kind === 'project')
    case 'unread':
      return items.filter((i) => i.unread)
    case 'queue':
      /*
       * Not a filter over these rows at all — the queue is a different list, rendered
       * by its own component, and the panel branches before it gets here.
       *
       * Named anyway, because `default: return items` would hand back the ENTIRE feed
       * under a heading that promises pending messages. Every past event, presented as
       * things that have not happened yet: the confident wrong answer, reachable the
       * day someone routes this tab through the filter by habit.
       */
      return []
    default:
      return items
  }
}

/**
 * "3h ago", Slack's own scale.
 *
 * Seconds in, because that is what both sources send. A millisecond timestamp
 * would land 55,000 years in the future and render as a plain date rather than
 * as anything obviously wrong, so the unit is asserted in the tests.
 */
export function relativeTime(ts: number, now: number): string {
  const d = Math.max(0, Math.floor(now - ts))
  // Past a week a duration stops being useful and a DATE starts being: "9d ago" is
  // arithmetic the reader has to do. That step needs the absolute timestamp, which is
  // why it stays here and the ladder does not.
  if (d < 7 * 86400) return relativeAge(d)
  return new Date(ts * 1000).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
